import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@growtherapy/sprout-ui';

import { logger } from '../../datadog/logger';
import { useGetTelehealthSessionInfoCallback } from '../../hooks';
import { useChatDrawerButtonUtils } from '../chat/useChatDrawerButtonUtils';
import { VisitorDrawerState, providerIsOnlineAtom, visitorDrawerStateAtom, visitorIsHelpModalOpenAtom } from '../state';
import { isProviderNotHereModalOpenAtom } from './admission/state';
import { useClearProviderNoShowTabTitleCallback } from './useClearProviderNoShowTabTitle';
import { useRecordProviderNoShowReportCallback } from './useRecordProviderNoShowReportCallback';

export function ProviderNotHereModal({
  isOpen,
  onClose,
  onReportProviderNoShow,
  onMessageProvider,
  isProviderOnline,
}: {
  isOpen: boolean;
  onClose: () => void;
  onReportProviderNoShow: () => void;
  isProviderOnline: boolean;
  onMessageProvider: () => void;
}) {
  const t = useTranslate();
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>{t('telehealth-core:waitingRoom.isYourProviderNotHereLetUsKnow')}</ModalHeader>
      <ModalBody aria-live='polite'>
        {isProviderOnline
          ? t('telehealth-core:waitingRoom.yourProviderAppearsToBeOnline')
          : t('telehealth-core:waitingRoom.weNoticedYourProviderHasntShownUp')}
      </ModalBody>
      <ModalFooter
        actions={[
          {
            key: 'continue-waiting',
            use: 'secondary',
            onClick: onClose,
            actionContent: t('telehealth-core:waitingRoom.continueWaiting'),
          },
          {
            key: 'report-provider-no-show',
            use: 'primary',
            onClick: isProviderOnline ? onMessageProvider : onReportProviderNoShow,
            actionContent: isProviderOnline
              ? t('telehealth-core:waitingRoom.messageProvider')
              : t('telehealth-core:waitingRoom.yesMyProviderIsntHere'),
          },
        ]}
      />
    </Modal>
  );
}

export function ProviderNotHereModalWrapper({
  onOpenModal,
  onCloseModal,
}: {
  onOpenModal?: () => void;
  onCloseModal?: () => void;
}) {
  const { providerShortId, patientShortId } = useParams();
  const [isProviderNotHereModalOpen, setIsProviderNotHereModalOpen] = useAtom(isProviderNotHereModalOpenAtom);

  const { isPastNoShowThreshold, recordProviderNoShowAndRedirect } = useRecordProviderNoShowReportCallback();
  const { toggleChatDrawer } = useChatDrawerButtonUtils();
  const clearProviderNoShowTabTitle = useClearProviderNoShowTabTitleCallback();
  const [fetchSessionInfo] = useGetTelehealthSessionInfoCallback();

  const getIsHelpModalOpen = useAtomCallback(useCallback((get) => get(visitorIsHelpModalOpenAtom), []));
  const isProviderOnline = useAtomValue(providerIsOnlineAtom);
  const getVisitorDrawerState = useAtomCallback(useCallback((get) => get(visitorDrawerStateAtom), []));

  useEffect(() => {
    if (!isPastNoShowThreshold || getIsHelpModalOpen()) return;

    // Before opening the no-show modal, do a fresh query to verify the
    // appointment is still active. If the appointment was cancelled mid-wait,
    // the server will return appointment: null and we skip the modal entirely.
    fetchSessionInfo({ variables: { providerShortId, patientShortId } })
      .then(({ data }) => {
        if (!data?.telehealthSessionInfo?.appointment) {
          logger.info('Skipping provider no-show modal — appointment no longer active', {
            providerShortId,
            patientShortId,
          });
          return;
        }
        setIsProviderNotHereModalOpen(true);
        onOpenModal?.();
      })
      .catch(() => {
        // If the check fails, fall back to showing the modal to avoid
        // silently swallowing a legitimate no-show scenario.
        setIsProviderNotHereModalOpen(true);
        onOpenModal?.();
      });
  }, [
    isPastNoShowThreshold,
    getIsHelpModalOpen,
    onOpenModal,
    setIsProviderNotHereModalOpen,
    fetchSessionInfo,
    providerShortId,
    patientShortId,
  ]);

  return (
    <ProviderNotHereModal
      isOpen={isProviderNotHereModalOpen}
      onClose={() => {
        clearProviderNoShowTabTitle();
        setIsProviderNotHereModalOpen(false);
        onCloseModal?.();
      }}
      onReportProviderNoShow={recordProviderNoShowAndRedirect}
      onMessageProvider={() => {
        clearProviderNoShowTabTitle();
        if (getVisitorDrawerState() !== VisitorDrawerState.CHAT) {
          toggleChatDrawer();
        }
        setIsProviderNotHereModalOpen(false);
      }}
      isProviderOnline={!!isProviderOnline}
    />
  );
}
